import { Badge } from "./components/Badge";
import { Button } from "./components/Button";
import assetHumanConnection from "./assets/asset-human-connection.png";
import assetMeditation from "./assets/asset-meditation.png";
import assetNatureDetail from "./assets/asset-nature-detail.png";
import assetHealthyFlatlay from "./assets/asset-healthy-flatlay.png";

const painPoints = [
  {
    title: "Disconnected records",
    text: "Your blood work, wearables, notes, and scans live in separate systems that never speak the same language.",
  },
  {
    title: "Generic recommendations",
    text: "Most advice is static and broad, not tailored to your longitudinal data and your actual behavior.",
  },
  {
    title: "Friction before every appointment",
    text: "Building a concise summary for your doctor still means manual exports, screenshots, and guesswork.",
  },
];

const benefitCards = [
  {
    title: "Own.",
    subtitle: "Health Vault",
    text: "One encrypted, portable vault where your health context compounds over time.",
    tag: "Data Ownership",
    tone: "from-kin-green-light/60 to-kin-white",
  },
  {
    title: "Understand.",
    subtitle: "AI Guides",
    text: "Run trusted expert protocols on your personal data and get clear next steps.",
    tag: "Personalized Insight",
    tone: "from-kin-lavender-light/65 to-kin-white",
  },
  {
    title: "Share.",
    subtitle: "On Your Terms",
    text: "Share only what matters with clinicians or family, and revoke access whenever you want.",
    tag: "Controlled Sharing",
    tone: "from-kin-gold-light/65 to-kin-white",
  },
];

const howItWorks = [
  {
    step: "01",
    title: "Connect everything once",
    text: "Upload records and sync sources. Kin builds your personal health graph in the background.",
  },
  {
    step: "02",
    title: "Run a guide built by experts",
    text: "Choose a protocol matched to your goal, then execute it directly on your vault.",
  },
  {
    step: "03",
    title: "Act with confidence",
    text: "Receive an explainable summary, concrete next actions, and doctor-ready output.",
  },
];

const trustItems = [
  {
    title: "User-controlled permissions",
    text: "You decide who can view, run, or share from your vault.",
  },
  {
    title: "Human-gated outcomes",
    text: "Kin guides decisions and does not replace clinical judgment.",
  },
  {
    title: "Transparent source context",
    text: "Insights can show what data they are based on and why.",
  },
];

function SectionHeading({ eyebrow, title, text, align = "left" }) {
  return (
    <div className={align === "center" ? "mx-auto max-w-3xl text-center" : "max-w-3xl"}>
      <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">{eyebrow}</p>
      <h2 className="mt-3 font-serif text-4xl font-bold leading-tight text-kin-black sm:text-5xl">
        {title}
      </h2>
      <p className="mt-4 text-base leading-relaxed text-kin-body/90 sm:text-lg">{text}</p>
    </div>
  );
}

function DataSourceIcon({ label, className = "h-3.5 w-3.5" }) {
  if (label === "Labs") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path
          d="M9 4.5h6M10.5 4.5v4.1L6.75 15a4.3 4.3 0 0 0 3.7 6.5h3.1a4.3 4.3 0 0 0 3.7-6.5l-3.75-6.4V4.5"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (label === "Wearables") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <rect
          x="7.5"
          y="5.75"
          width="9"
          height="12.5"
          rx="2.5"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <path
          d="M10 2.75h4M10 21.25h4"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (label === "CGM") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path
          d="M3.75 12h3l2-3.5 3.5 7 2.5-5.5h5.5"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (label === "Supplements") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path
          d="M8.2 8.2a4 4 0 0 1 5.65 0l2 2a4 4 0 1 1-5.65 5.65l-2-2a4 4 0 0 1 0-5.65Z"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <path d="m10 10 4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }

  if (label === "Notes") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path
          d="M7 4.75h8.25L18 7.5v11.75H7V4.75Z"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
        <path
          d="M15.25 4.75V7.5H18M9 11h6M9 14h6"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (label === "Genetics") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
        <path
          d="M8 5.5c6.5 0 8 13 0 13M16 5.5c-6.5 0-8 13 0 13"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
        <path
          d="M8.5 9h7M8.5 12h7M8.5 15h7"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  return null;
}

function SourceOrbit() {
  const nodes = [
    { label: "Labs", detail: "6 reports", x: 18, y: 20, tone: "text-kin-gold-dark" },
    { label: "Wearables", detail: "Oura + Apple", x: 80, y: 20, tone: "text-kin-green-dark" },
    { label: "CGM", detail: "14 days", x: 11, y: 49, tone: "text-kin-orange" },
    { label: "Supplements", detail: "Daily log", x: 85, y: 48, tone: "text-kin-lavender" },
    { label: "Notes", detail: "Clinical notes", x: 20, y: 78, tone: "text-kin-muted" },
    { label: "Genetics", detail: "DNA markers", x: 74, y: 82, tone: "text-kin-green-dark" },
  ];

  const center = { x: 50, y: 48 };

  const connectorPath = (x, y) => {
    const pullX = x < center.x ? -8 : 8;
    const pullY = y < center.y ? -7 : 7;
    const midX = (x + center.x) / 2 + pullX;
    const midY = (y + center.y) / 2 + pullY;
    return `M ${x} ${y} Q ${midX} ${midY}, ${center.x} ${center.y}`;
  };

  return (
    <article className="kin-surface kin-surface-clouds relative overflow-hidden rounded-[30px] p-6 sm:p-8">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(58% 48% at 82% 12%, rgba(227,218,255,0.45) 0%, rgba(227,218,255,0) 72%), radial-gradient(52% 46% at 14% 88%, rgba(255,226,138,0.3) 0%, rgba(255,226,138,0) 74%), linear-gradient(160deg, rgba(255,255,255,0.82) 0%, rgba(245,251,247,0.56) 100%)",
        }}
      />

      <div className="relative z-10 flex items-center justify-between">
        <p className="text-[11px] font-semibold tracking-[0.16em] uppercase text-kin-muted">
          Before and After
        </p>
        <Badge className="normal-case tracking-normal">6 sources connected</Badge>
      </div>

      <div className="relative mt-4 h-[24.5rem] sm:h-[25rem]">
        <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full">
          <defs>
            <radialGradient id="kin-orbit-core" cx="50%" cy="50%" r="65%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.94)" />
              <stop offset="65%" stopColor="rgba(255,255,255,0.18)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0)" />
            </radialGradient>
            <linearGradient id="kin-orbit-line" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgba(58,74,61,0.28)" />
              <stop offset="100%" stopColor="rgba(58,74,61,0.09)" />
            </linearGradient>
          </defs>

          <circle
            cx="50"
            cy="48"
            r="24"
            fill="none"
            stroke="rgba(58,74,61,0.16)"
            strokeWidth="0.24"
          />
          <circle
            cx="50"
            cy="48"
            r="18.2"
            fill="none"
            stroke="rgba(58,74,61,0.11)"
            strokeWidth="0.24"
          />
          <circle cx="50" cy="48" r="29.5" fill="url(#kin-orbit-core)" />

          {nodes.map((node) => (
            <path
              key={node.label}
              d={connectorPath(node.x, node.y)}
              stroke="url(#kin-orbit-line)"
              strokeWidth="0.34"
              fill="none"
              strokeDasharray="1.05 1.05"
            />
          ))}

          {nodes.map((node) => (
            <circle
              key={`${node.label}-dot`}
              cx={node.x}
              cy={node.y}
              r="0.8"
              fill="rgba(58,74,61,0.34)"
            />
          ))}
        </svg>

        <div className="absolute left-1/2 top-[48%] z-20 h-[9.5rem] w-[9.5rem] -translate-x-1/2 -translate-y-1/2 rounded-full border border-kin-olive/14 bg-gradient-to-br from-kin-green-light/78 via-kin-white to-kin-lavender-light/72 shadow-[0_34px_48px_-34px_rgba(58,74,61,0.88)]">
          <div className="flex h-full flex-col items-center justify-center rounded-full backdrop-blur-[1px]">
            <p className="text-[10px] font-semibold tracking-[0.11em] uppercase text-kin-muted">
              Unified Runtime
            </p>
            <p className="mt-1 text-center text-3xl font-semibold leading-[0.95] text-kin-black">
              Kin
              <br />
              Vault
            </p>
          </div>
        </div>

        <div className="absolute left-1/2 top-[48%] h-[14rem] w-[14rem] -translate-x-1/2 -translate-y-1/2 rounded-full border border-kin-olive/8" />
        <div className="absolute left-1/2 top-[48%] h-[17.5rem] w-[17.5rem] -translate-x-1/2 -translate-y-1/2 rounded-full border border-kin-olive/6" />

        {nodes.map((node) => (
          <div
            key={node.label}
            className="absolute z-10 -translate-x-1/2 -translate-y-1/2"
            style={{
              left: `${node.x}%`,
              top: `${node.y}%`,
            }}
          >
            <div className="flex min-w-[7.5rem] items-center gap-2 rounded-full border border-kin-olive/14 bg-kin-white/92 px-3 py-2 shadow-[0_14px_26px_-22px_rgba(58,74,61,0.9)] backdrop-blur-sm">
              <span
                className={`inline-flex h-7 w-7 items-center justify-center rounded-full border border-kin-olive/12 bg-kin-white ${node.tone}`}
              >
                <DataSourceIcon label={node.label} />
              </span>
              <span>
                <span className="block text-[11px] font-semibold tracking-[0.08em] uppercase text-kin-body">
                  {node.label}
                </span>
                <span className="block text-[10px] text-kin-muted">{node.detail}</span>
              </span>
            </div>
          </div>
        ))}

        <div className="absolute bottom-3 left-2 rounded-lg border border-kin-olive/12 bg-kin-white/86 px-3 py-1.5 text-[10px] text-kin-muted">
          Fragmented inputs
        </div>
        <div className="absolute bottom-3 right-2 rounded-lg border border-kin-olive/12 bg-kin-white/86 px-3 py-1.5 text-[10px] text-kin-muted">
          Unified context
        </div>
      </div>
    </article>
  );
}

export default function LandingPage() {
  return (
    <div className="relative min-h-screen overflow-x-clip bg-kin-sunrise">
      <div className="pointer-events-none fixed inset-0 -z-30 bg-kin-sunrise animate-drift" />
      <div className="pointer-events-none fixed -left-28 -top-24 -z-20 h-[34rem] w-[34rem] rounded-full bg-kin-lavender/20 blur-3xl" />
      <div className="pointer-events-none fixed right-[-8rem] top-28 -z-20 h-[30rem] w-[30rem] rounded-full bg-kin-gold/15 blur-3xl" />
      <div className="pointer-events-none fixed bottom-[-9rem] left-1/3 -z-20 h-[26rem] w-[26rem] rounded-full bg-kin-orange/15 blur-3xl" />

      <header className="px-6 pt-6 sm:px-8">
        <div className="mx-auto flex max-w-6xl items-center justify-between rounded-2xl border border-kin-olive/12 bg-kin-white/78 px-4 py-3 backdrop-blur-md sm:px-5">
          <div>
            <p className="font-serif text-2xl font-bold leading-none text-kin-black">Kin</p>
            <p className="mt-1 text-[10px] font-semibold tracking-[0.14em] uppercase text-kin-muted">
              Your Health, Intelligent
            </p>
          </div>

          <div className="flex items-center gap-2">
            <a href="/private/design/mobile">
              <Button variant="secondary" size="small">
                Mobile
              </Button>
            </a>
            <a href="/private/design">
              <Button variant="secondary" size="small">
                Brandbook
              </Button>
            </a>
            <a href="#join">
              <Button size="small">Create Account</Button>
            </a>
          </div>
        </div>
      </header>

      <main className="px-6 pb-16 pt-10 sm:px-8 sm:pb-20 sm:pt-14">
        <section className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[1.03fr_0.97fr] lg:items-center">
          <div>
            <Badge variant="lavender">Private beta now open</Badge>
            <h1 className="mt-4 font-serif text-6xl font-bold leading-[0.95] tracking-tight text-kin-black sm:text-7xl md:text-8xl">
              Stop juggling your health data.
            </h1>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-kin-body/92">
              Kin brings your records together, runs expert guides on your own vault, and gives you
              a clear plan you can actually use.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <a href="#join">
                <Button size="large">Create an Account</Button>
              </a>
              <a href="#product">
                <Button variant="secondary" size="large">
                  Explore Product
                </Button>
              </a>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <Badge className="normal-case tracking-normal">Encrypted vault</Badge>
              <Badge variant="lavender" className="normal-case tracking-normal">
                Expert AI guides
              </Badge>
              <Badge variant="gold" className="normal-case tracking-normal">
                Doctor-ready sharing
              </Badge>
            </div>
          </div>

          <article className="relative h-[34rem]">
            <div className="kin-surface-strong kin-surface-clouds absolute inset-0 overflow-hidden rounded-[36px] p-5 sm:p-6">
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage:
                    "radial-gradient(65% 56% at 82% 12%, rgba(227,218,255,0.45) 0%, rgba(227,218,255,0) 72%), radial-gradient(55% 45% at 12% 88%, rgba(255,226,138,0.35) 0%, rgba(255,226,138,0) 70%)",
                }}
              />

              <img
                src={assetHumanConnection}
                alt="Person reviewing health insights"
                className="absolute left-6 top-6 h-56 w-44 rounded-[22px] object-cover shadow-[0_24px_40px_-24px_rgba(27,29,26,0.8)]"
              />

              <img
                src={assetHealthyFlatlay}
                alt="Healthy lifestyle composition"
                className="absolute right-7 top-14 h-48 w-40 rounded-[22px] object-cover shadow-[0_20px_34px_-22px_rgba(27,29,26,0.75)]"
              />

              <img
                src={assetMeditation}
                alt="Calm wellness moment"
                className="absolute bottom-7 left-24 h-44 w-56 rounded-[22px] object-cover shadow-[0_22px_36px_-24px_rgba(27,29,26,0.78)]"
              />

              <div className="absolute bottom-8 right-7 w-44 rounded-2xl border border-kin-olive/14 bg-kin-white/90 p-3 backdrop-blur-sm">
                <p className="text-[10px] font-semibold tracking-[0.14em] uppercase text-kin-muted">
                  This Week
                </p>
                <p className="mt-1 text-sm font-semibold text-kin-body">Recovery +12%</p>
                <p className="mt-0.5 text-xs text-kin-muted">Doctor summary ready</p>
              </div>
            </div>
          </article>
        </section>

        <section className="mx-auto mt-20 grid max-w-6xl gap-6 lg:grid-cols-[1fr_1.05fr]">
          <div>
            <SectionHeading
              eyebrow="User Needs"
              title="The current experience is fragmented."
              text="Kin is designed for people who are tired of piecing together their health story by hand."
            />

            <div className="mt-6 space-y-3">
              {painPoints.map((item, index) => (
                <article
                  key={item.title}
                  className={`kin-surface rounded-2xl p-4 ${
                    index === 1 ? "kin-surface-splash" : "kin-surface-waves"
                  }`}
                >
                  <p className="text-sm font-semibold text-kin-black">{item.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-kin-body/90">{item.text}</p>
                </article>
              ))}
            </div>
          </div>

          <SourceOrbit />
        </section>

        <section id="product" className="mx-auto mt-20 max-w-6xl">
          <SectionHeading
            eyebrow="Product"
            title="Own. Understand. Share."
            text="A single system that turns your health data into private, personalized, and practical action."
            align="center"
          />

          <div className="mt-8 grid gap-4 md:grid-cols-6 md:grid-rows-2">
            {benefitCards.map((item, index) => (
              <article
                key={item.title}
                className={`kin-surface relative overflow-hidden rounded-3xl p-6 ${
                  index === 0 ? "md:col-span-3 md:row-span-2" : "md:col-span-3"
                }`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${item.tone}`} />
                <div className="relative">
                  <Badge variant={item.variant}>{item.tag}</Badge>
                  <h3 className="mt-4 font-serif text-5xl font-bold leading-none text-kin-black">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm font-semibold tracking-[0.08em] uppercase text-kin-muted">
                    {item.subtitle}
                  </p>
                  <p className="mt-3 max-w-md text-sm leading-relaxed text-kin-body/90">
                    {item.text}
                  </p>

                  {index === 0 && (
                    <img
                      src={assetNatureDetail}
                      alt="Organic detail texture"
                      className="mt-6 h-44 w-full rounded-2xl object-cover"
                    />
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="mx-auto mt-20 max-w-6xl">
          <SectionHeading
            eyebrow="How It Works"
            title="From upload to useful action"
            text="Three clear steps, one continuous timeline."
          />

          <div className="mt-8 grid gap-3 lg:grid-cols-3">
            {howItWorks.map((item, index) => (
              <article
                key={item.step}
                className={`kin-surface rounded-3xl p-5 ${
                  index === 0
                    ? "kin-surface-clouds"
                    : index === 1
                      ? "kin-surface-splash"
                      : "kin-surface-waves"
                }`}
              >
                <p className="text-[11px] font-semibold tracking-[0.16em] uppercase text-kin-muted">
                  Step {item.step}
                </p>
                <h3 className="mt-2 text-xl font-semibold text-kin-black">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-kin-body/90">{item.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mx-auto mt-20 grid max-w-6xl gap-4 xl:grid-cols-[0.92fr_1.08fr]">
          <article className="kin-surface kin-surface-waves rounded-3xl p-6">
            <p className="text-xs font-semibold tracking-[0.16em] uppercase text-kin-muted">
              Output Preview
            </p>
            <h3 className="mt-3 font-serif text-3xl font-bold leading-tight text-kin-black">
              A concise summary for real decisions.
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-kin-body/90">
              Kin translates complex records into focused results you can discuss with confidence.
            </p>

            <div className="mt-5 space-y-2">
              {[
                ["Biomarker timeline", "18 months"],
                ["Protocol score", "72 / 100"],
                ["Gaps found", "3 high-priority"],
                ["Doctor share", "QR ready"],
              ].map(([label, value]) => (
                <div
                  key={label}
                  className="flex items-center justify-between rounded-xl border border-kin-olive/12 bg-kin-white/80 px-3 py-3"
                >
                  <p className="text-sm text-kin-body">{label}</p>
                  <p className="text-sm font-semibold text-kin-black">{value}</p>
                </div>
              ))}
            </div>
          </article>

          <article className="kin-surface kin-surface-clouds rounded-3xl p-6">
            <p className="text-xs font-semibold tracking-[0.16em] uppercase text-kin-muted">
              Trust and Safety
            </p>
            <div className="mt-4 space-y-3">
              {trustItems.map((item) => (
                <div
                  key={item.title}
                  className="rounded-xl border border-kin-olive/12 bg-kin-white/82 px-3 py-3"
                >
                  <p className="text-sm font-semibold text-kin-black">{item.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-kin-body/88">{item.text}</p>
                </div>
              ))}
            </div>
          </article>
        </section>

        <section id="join" className="mx-auto mt-20 max-w-6xl">
          <article className="kin-surface-strong relative overflow-hidden rounded-[34px] p-7 sm:p-10">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  "linear-gradient(135deg, rgba(227,218,255,0.62) 0%, rgba(255,255,255,0.86) 36%, rgba(255,226,138,0.48) 68%, rgba(255,138,77,0.28) 100%)",
              }}
            />
            <div className="relative grid gap-6 lg:grid-cols-[1fr_auto] lg:items-center">
              <div>
                <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">
                  Get Early Access
                </p>
                <h2 className="mt-3 font-serif text-4xl font-bold leading-tight text-kin-black sm:text-5xl">
                  Join Kin and make your health data finally useful.
                </h2>
                <p className="mt-4 max-w-2xl text-base leading-relaxed text-kin-body/92 sm:text-lg">
                  Start with your own vault, run expert guides, and walk into appointments with a
                  sharper story and better decisions.
                </p>
                <p className="mt-4 text-xs text-kin-muted">
                  Kin supports clinician workflows but does not replace diagnosis or emergency care.
                </p>
              </div>

              <div className="flex flex-wrap gap-3 lg:flex-col lg:items-end">
                <a href="mailto:sverre@kin.health?subject=Kin%20Early%20Access%20Request">
                  <Button size="large">Create an Account</Button>
                </a>
                <a href="mailto:sverre@kin.health?subject=Kin%20Product%20Questions">
                  <Button variant="secondary" size="large">
                    Talk to the Team
                  </Button>
                </a>
              </div>
            </div>
          </article>
        </section>
      </main>
    </div>
  );
}
